import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Understanding Socio-Relational Well-Being',
    content: [
      '<strong>Learning Goal:</strong> Define socio-relational well-being, understand its importance in holistic health, and identify your personal sources of social support.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• What is socio-relational well-being?',
      '• The role of human connection in mental health',
      '• Psychological foundations: attachment theory and social bonding',
      '• Overview of the MSPSS model',
      '<br/>',
      '<strong>Discussion:</strong> Socio-relational well-being is about the quality of your connections with others. It\'s not just having people around–it\'s feeling supported, valued, and connected in meaningful ways.'
    ]
  },
  {
    title: 'What is Socio-Relational Well-Being?',
    content: [
      'Socio-relational well-being refers to your sense of connection, belonging, and support from others. It encompasses:',
      '<br/>',
      '<strong>Emotional Support:</strong> Feeling cared for and understood by others',
      '• Having someone to talk to during difficult times',
      '• Receiving empathy and compassion',
      '• Feeling emotionally safe in relationships',
      '<br/>',
      '<strong>Social Belonging:</strong> Feeling like you are part of a community or group',
      '• Having a sense of identity within social circles',
      '• Feeling accepted and valued by others',
      '• Experiencing meaningful connections',
      '<br/>',
      '<strong>Practical Support:</strong> Receiving help with daily challenges',
      '• Getting assistance with tasks or problems',
      '• Having access to resources through your network',
      '• Feeling that others have your back',
      '<br/>',
      '<strong>Why It Matters:</strong> Strong socio-relational well-being acts as a buffer against stress, depression, and anxiety. It enhances resilience and promotes overall mental health.'
    ]
  },
  {
    title: 'Psychological Foundations',
    content: [
      'Socio-relational well-being is rooted in several psychological theories and concepts:',
      '<br/>',
      '<strong>Attachment Theory:</strong> Developed by John Bowlby, this theory explains how early relationships shape our ability to form secure connections throughout life.',
      '• Secure attachment: Feeling safe to express needs and emotions',
      '• Anxious attachment: Fear of abandonment, constant need for reassurance',
      '• Avoidant attachment: Difficulty trusting others, preference for independence',
      '<br/>',
      '<strong>Social Bonding:</strong> Humans are inherently social creatures. Research shows that social isolation can be as harmful to health as smoking or obesity.',
      '• Oxytocin release during positive social interactions',
      '• Mirror neurons that help us understand others\' emotions',
      '• Social brain networks that process relationship information',
      '<br/>',
      '<strong>Community Psychology:</strong> Focuses on how individuals function within their social contexts and communities.',
      '• Sense of community: Feeling connected to a larger social group',
      '• Social capital: Resources available through social networks',
      '• Collective efficacy: Belief in community\'s ability to solve problems together'
    ]
  },
  {
    title: 'The MSPSS Model',
    content: [
      'The Multidimensional Scale of Perceived Social Support (MSPSS) measures perceived support across three key areas:',
      '<br/>',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header pillar-cell">SUPPORT TYPE</div>
          <div class="table-header means-cell">DESCRIPTION</div>
          <div class="table-header matters-cell">EXAMPLES</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Significant Other</div>
          <div class="table-cell means-cell">Support from a romantic partner or closest confidant</div>
          <div class="table-cell matters-cell">Emotional intimacy, trust, reliable care</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Family</div>
          <div class="table-cell means-cell">Support from family members and relatives</div>
          <div class="table-cell matters-cell">Shared responsibility, guidance, belonging</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">Friends</div>
          <div class="table-cell means-cell">Support from friends and peer relationships</div>
          <div class="table-cell matters-cell">Empathy, shared experiences, companionship</div>
        </div>
      </div>`,
      '<br/>',
      '<strong>Key Insight:</strong> The MSPSS focuses on <em>perceived</em> support–what you believe is available to you–rather than actual support received. Your perception of support often matters more than the reality.'
    ]
  },
  {
    title: 'Personal Reflection Activity',
    content: [
      '<strong>Reflection Map Exercise:</strong> Take a moment to identify your current sources of social support.',
      '<br/>',
      '<strong>Step 1:</strong> List your top 3 sources of emotional support in your life.',
      '<strong>Step 2:</strong> For each person, categorize them as:',
      '• Significant Other (romantic partner, closest confidant)',
      '• Family (relatives, family members)',
      '• Friends (peers, acquaintances)',
      '<br/>',
      '<strong>Step 3:</strong> Reflect on these questions:',
      '• How do these relationships help you during stressful times?',
      '• Which category feels strongest in your support network?',
      '• Which category might need more attention or development?',
      '<br/>',
      '<strong>Takeaway:</strong> This exercise helps you become aware of your existing support system and identify areas for growth. Remember, it\'s normal for support networks to change over time.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 1: Understanding Socio-Relational Well-Being! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about socio-relational well-being concepts.',
        'The quiz will help you review key concepts about understanding your social support networks.'
      ]
    }
  }
];

const SocioRelationalModule1Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('socioRelationalM1');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/socio-relational-module-1/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/socio-relational-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left socio-relational-panel"
        >
          <div className="module-header">
            <h1>Module 1</h1>
            <p className="module-subtitle">Understanding Socio-Relational Well-Being</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Define socio-relational well-being, understand its importance in holistic health, and identify your personal sources of social support.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/socio-relational-module-1/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocioRelationalModule1Page;

